/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { fetchCloudPerformanceAPI } from 'kontrolEdge/components/CloudConnectivity/requests/request';
import { fetchConnectionPerformanceAPI } from 'kontrolEdge/components/Internet/requests/request';

interface BandwidthItem {
  time: string;
  value: number;
}

interface BandwidthUtilization {
  inbound: BandwidthItem[];
  outbound: BandwidthItem[];
  timeRange: string;
  interval: string;
  unit: string;
}

interface LatencyDetail {
  destination: string;
  timestamp: string;
  unit: string;
  value: number
}

interface ConnectionPerformanceData {
  connectionId: number;
  bandwidthUtilization: BandwidthUtilization;
  latencyReadings: LatencyDetail[];
}

interface CloudPerformanceData {
  connectionId: number;
  bandwidthUtilization: BandwidthUtilization;
  latencyReadings: LatencyDetail[];
}

// Extend the state to include new properties
interface ConnectionState {
  lag: any;
  performanceData: ConnectionPerformanceData | null;
  performanceLoading: boolean;
  performanceError: string | null;
  timeRange: string;
  interval: string;
  selectedLatencyDest: string | null;
  // Custom date range mode
  useCustomDateRange: boolean;
  customStartDate: string | null; // ISO string
  customEndDate: string | null;   // ISO string
  maxPoints: number;
}

const initialState: ConnectionState = {
  lag: null,
  performanceData: null,
  performanceLoading: false,
  performanceError: null,
  timeRange: '5m',
  interval: '5s',
  selectedLatencyDest: null,
  useCustomDateRange: false,
  customStartDate: null,
  customEndDate: null,
  maxPoints: 180,
};

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    setLag: (state, action: PayloadAction<any>) => {
      state.lag = action.payload;
    },
    clearLag: (state) => {
      state.lag = null;
    },
    // Reducers for updating filters
    setTimeRange: (state, action: PayloadAction<string>) => {
      state.timeRange = action.payload;
    },
    setInterval: (state, action: PayloadAction<string>) => {
      state.interval = action.payload;
    },
    setSelectedLatencyDest: (state, action: PayloadAction<string | null>) => {
      state.selectedLatencyDest = action.payload;
    },
    // Custom date range reducers
    setUseCustomDateRange: (state, action: PayloadAction<boolean>) => {
      state.useCustomDateRange = action.payload;
      if (!action.payload) {
        state.customStartDate = null;
        state.customEndDate = null;
      }
    },
    setCustomDateRange: (state, action: PayloadAction<{ start: string; end: string }>) => {
      state.customStartDate = action.payload.start;
      state.customEndDate = action.payload.end;
    },
    clearCustomDateRange: (state) => {
      state.customStartDate = null;
      state.customEndDate = null;
    },
    setMaxPoints: (state, action: PayloadAction<number>) => {
      state.maxPoints = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectionPerformance.pending, (state) => {
        state.performanceLoading = true;
        state.performanceError = null;
      })
      .addCase(fetchConnectionPerformance.fulfilled, (state, action: PayloadAction<ConnectionPerformanceData>) => {
        state.performanceData = action.payload;
        state.performanceLoading = false;
      })
      .addCase(fetchConnectionPerformance.rejected, (state, action) => {
        state.performanceLoading = false;
        state.performanceError = action.payload as string;
      })
    .addCase(fetchCloudPerformance.pending, (state) => {
        state.performanceLoading = true;
        state.performanceError = null;
      })
      .addCase(fetchCloudPerformance.fulfilled, (state, action: PayloadAction<CloudPerformanceData>) => {
        state.performanceData = action.payload;
        state.performanceLoading = false;
      })
      .addCase(fetchCloudPerformance.rejected, (state, action) => {
        state.performanceLoading = false;
        state.performanceError = action.payload as string;
      });
    }
    
});

export const fetchConnectionPerformance = createAsyncThunk<
ConnectionPerformanceData,
  { 
    api: AxiosInstance; 
    connectionId: string; 
    timeRange?: string; 
    interval: string; 
    tenantId: number; 
    destination?: string;
    startDate?: string;  // ISO string
    endDate?: string;    // ISO string
    maxPoints?: number;
  },
  { rejectValue: string }
>(
  'internet/fetchPerformance',
  async ({ api, connectionId, timeRange, interval, tenantId, destination, startDate, endDate, maxPoints }, { rejectWithValue }) => {
    try {
      const response = await fetchConnectionPerformanceAPI(
        api, 
        connectionId, 
        timeRange, 
        interval, 
        tenantId, 
        destination,
        startDate,
        endDate,
        maxPoints
      );
      return response.data.data as CloudPerformanceData;
    } catch (err) {
      console.error("Error fetching performance data", err);
      return rejectWithValue("Failed to fetch performance data");
    }
  }
);


export const fetchCloudPerformance = createAsyncThunk<
CloudPerformanceData,
  { 
    api: AxiosInstance; 
    connectionId: string; 
    timeRange?: string; 
    interval: string; 
    tenantId: number;
    startDate?: string;  // ISO string
    endDate?: string;    // ISO string
    maxPoints?: number;
  },
  { rejectValue: string }
>(
  'cloud/fetchPerformance',
  async ({ api, connectionId, timeRange, interval, tenantId, startDate, endDate, maxPoints }, { rejectWithValue }) => {
    try {
      const response = await fetchCloudPerformanceAPI(
        api, 
        connectionId, 
        timeRange, 
        interval, 
        tenantId,
        startDate,
        endDate,
        maxPoints
      );
      return response.data.data as ConnectionPerformanceData;
    } catch (err) {
      console.error("Error fetching performance data", err);
      return rejectWithValue("Failed to fetch performance data");
    }
  }
);

export const { 
  setLag, 
  clearLag, 
  setInterval, 
  setTimeRange, 
  setSelectedLatencyDest,
  setUseCustomDateRange,
  setCustomDateRange,
  clearCustomDateRange,
  setMaxPoints
} = connectionSlice.actions;
export default connectionSlice.reducer;